// frontend/src/pages/LandingPage.tsx — MinikCoder Academy AI Destekli Öğrenme Platformu

import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

// Ödeme sistemi entegre edilene kadar plan tipi burada tanımlanır
interface Plan {
  id: string;
  name: string;
  tier: string;
  price_try: number;
  type: string;
  description?: string;
  badge_label?: string;
  features?: string[];
  limits?: { daily_ai_messages?: number | null };
}

import {
  Code2,
  Trophy,
  Users,
  ArrowRight,
  Play,
  RotateCcw,
  CheckCircle2,
  Sparkles,
  Bot,
  Star,
  Activity,
  ChevronDown,
  Mail,
  Globe,
  MapPin,
  Send,
  Zap,
  Flame,
  Award,
  ShieldCheck,
  Compass,
  Check
} from 'lucide-react';
import '../styles/landing.css';
import { ShaderBackground } from '../components/ui/manu';

// Official MinikCoder Akademi Logo SVG Component
export const MinikCoderLogo = ({ size = 36 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 200 200"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ filter: 'drop-shadow(0 2px 10px rgba(23, 199, 154, 0.4))' }}
  >
    <defs>
      <linearGradient id="landingLogoBlue" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#38BDF8" />
        <stop offset="50%" stopColor="#0284C7" />
        <stop offset="100%" stopColor="#0369A1" />
      </linearGradient>
      <linearGradient id="landingLogoTeal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#17C79A" />
        <stop offset="100%" stopColor="#0B8F70" />
      </linearGradient>
    </defs>
    <path
      d="M 35 150 L 35 60 L 80 110 L 110 80 L 145 120 L 145 150"
      stroke="url(#landingLogoBlue)"
      strokeWidth="18"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M 110 60 L 155 60 C 170 60 170 85 150 100 L 115 125 L 155 170"
      stroke="url(#landingLogoTeal)"
      strokeWidth="18"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="95" cy="95" r="14" fill="#17C79A" />
    <path d="M 68 128 Q 95 102 122 128" stroke="#38BDF8" strokeWidth="12" strokeLinecap="round" />
    <rect x="150" y="25" width="12" height="12" rx="2" fill="#17C79A" />
    <rect x="168" y="10" width="8" height="8" rx="1.5" fill="#38BDF8" />
    <rect x="170" y="32" width="6" height="6" rx="1" fill="#0B8F70" />
  </svg>
);

// Feature display mapper for dynamic plans
const FEATURE_DISPLAY: Record<string, string> = {
  'course.access': 'Temel Ders İçerikleri',
  'course.premium': 'Tüm Premium Dersler',
  'lesson.preview': 'Ders Önizleme Hakları',
  'lesson.premium': 'Sınırsız Tüm Dersler',
  'ai.chat': 'Günde 10 YZ Mesajı',
  'ai.mentor': 'Sınırsız YZ Mentorluk',
  'ai.code_review': 'YZ Kod İncelemesi',
  'ai.code_fix': 'YZ Otomatik Kod Düzeltme',
  'ai.project_generator': 'YZ Proje Üretici',
  'ai.quiz_generator': 'YZ Sınav/Quiz Üretici',
  'ai.learning_plan': 'YZ Özel Öğrenme Planı',
  'certificate.generate': 'Doğrulanmış Sertifikalar',
  'hackathon.join': 'Hackathon Katılımı',
  'parent.report': 'Haftalık YZ Veli Raporu',
  'teacher.panel': 'Sınıf ve Öğretmen Paneli',
  'school.dashboard': 'Kurumsal Okul Paneli',
  'student.management': 'Gelişmiş Öğrenci Analitiği',
  'adventure.mode': 'Adventure Mode 🗺️',
};

function formatFeatureName(key: string): string {
  if (FEATURE_DISPLAY[key]) return FEATURE_DISPLAY[key];
  const normalized = key.toLowerCase().replace(/[._]/g, ' ').trim();
  if (FEATURE_DISPLAY[normalized]) return FEATURE_DISPLAY[normalized];
  return normalized
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Hero Interactive Slide Data (5 Branş: Python, Matematik, Fen, Fizik, Lise Matematiği)
const HERO_SLIDES = [
  {
    tag: 'SLAYT 1 / 5 — PYTHON KODLAMA',
    title: 'For Döngüsü & Karakter Hareketi',
    desc: 'Tekrarlayan işleri tek satıra indiriyoruz. Robotumuz her döngü adımında bir yıldız toplayarak hedefe ulaşır.',
    svgType: 'python'
  },
  {
    tag: 'SLAYT 2 / 5 — GÖRSEL MATEMATİK',
    title: 'Pisagor Teoremi (a² + b² = c²)',
    desc: 'Dik kenar kareleri (9 + 16 = 25) hipotenüs alanına eşittir. Ezberletmek yerine geometrinin mantığını gösteriyoruz.',
    svgType: 'math'
  },
  {
    tag: 'SLAYT 3 / 5 — FEN BİLİMLERİ & UZAY',
    title: 'Dünya Neden Dönüyor?',
    desc: 'Gezegenimiz Güneş etrafında elips bir yörüngede ilerler. CinAli çizimlerle kütle çekim kuvvetini canlandırır.',
    svgType: 'planet'
  },
  {
    tag: 'SLAYT 4 / 5 — FİZİK & MEKANİK',
    title: 'Serbest Düşüş & Yerçekimi İvmesi',
    desc: 'g = 9.8 m/s² ivmeyle düşen cismin hız ve konumu (v = g · t, h = ½gt²) saniye saniye hesaplanır ve çizilir.',
    svgType: 'physics'
  },
  {
    tag: 'SLAYT 5 / 5 — LİSE MATEMATİĞİ',
    title: 'İkinci Dereceden Fonksiyonlar & Parabol',
    desc: 'f(x) = x² - 2x - 3 parabol eğrisinin kolları, kökleri ve tepe noktası T(1, -4) koordinat düzleminde modellenir.',
    svgType: 'parabola'
  }
];

// CinAli Deep Dive Showcase Lessons (5 Branş: Python, Matematik, Fen, Fizik, Lise Matematiği)
const CİNALİ_TOPICS = [
  {
    id: 'python',
    tag: 'PYTHON & YAZILIM',
    title: '1. Döngüler ve Karar Yapıları (If-Else)',
    normalText: 'Python\'da "for i in range(3): robot.ilerle()" komutu ile tekrarlı adımları çalıştırırız. Şart bloklarıyla robot engelleri algılar.',
    simpleText: 'Robotuna "önünde taş varsa zıpla, yoksa koş!" diye emir veriyorsun. O da senin dediklerini harfiyen yapıp hedefe ulaşıyor!',
    svgType: 'python'
  },
  {
    id: 'math',
    tag: 'GÖRSEL MATEMATİK',
    title: '2. Pisagor Teoremi & Alan İspatları (a² + b² = c²)',
    normalText: 'Bir dik üçgende 3, 4 ve 5 birimlik kenarların kareleri toplamı (9 + 16 = 25) hipotenüs alanını verir. Geometri şekillerin kusursuz dengesidir.',
    simpleText: 'İki küçük Lego karesini birleştirip tam olarak büyük eğik kenara oturttuğunu hayal et! Şekiller yapboz gibi birleşir.',
    svgType: 'math'
  },
  {
    id: 'solar',
    tag: 'FEN BİLİMLERİ',
    title: '3. Güneş Sistemi & Gezegen Yörüngeleri',
    normalText: 'Güneş sistemimizde 8 gezegen bulunur. Her biri kütle çekim kuvvetiyle Güneş çevresinde eliptik yörüngede döner. Dünya ideal sıcaklık kuşağındadır.',
    simpleText: 'Güneş sınıftaki neşeli bir öğretmen, gezegenler de onun etrafında el ele tutuşup dönen öğrenciler gibi! Dünya tam serin ve güzel bir yerde.',
    svgType: 'solar'
  },
  {
    id: 'physics',
    tag: 'FİZİK & MEKANİK',
    title: '4. Newton\'ın Hareket Yasası (F = m · a)',
    normalText: 'Bir cisme uygulanan net kuvvet, cismin kütlesi ile ivmesinin çarpımına eşittir (F = m·a). Kütle sabitken kuvvet arttıkça ivme artar.',
    simpleText: 'Boş bir alışveriş arabasını itmek çok kolaydır ve hızla gider, ama içi ağır eşyalarla doluyken hızlandırmak için daha çok itmen gerekir!',
    svgType: 'physics'
  },
  {
    id: 'parabola',
    tag: 'LİSE MATEMATİĞİ',
    title: '5. Parabol Grafikleri & Tepe Noktası (T(r, k))',
    normalText: 'İkinci dereceden f(x) = ax² + bx + c fonksiyonlarının grafiği paraboldür. Tepe noktası T(r, k) = (-b/2a, f(r)) ekstremum değerini belirler.',
    simpleText: 'Basketbol topunu potaya fırlattığında havada çizdiği o güzel kavisli yolu düşün! Tam o en tepe nokta parabolün zirvesidir.',
    svgType: 'parabola'
  }
];

import { getPlanFeatures, PlanFeatureItem } from '../utils/planFeatures';

// Default Landing Plans Fallback
const DEFAULT_LANDING_PLANS: Plan[] = [
  {
    id: 'free-plan',
    name: 'Ücretsiz',
    tier: 'free',
    price_try: 0,
    type: 'individual',
    description: 'Platformu keşfetmek ve CinAli yapay zeka öğretmenini tanımak için ideal başlangıç.',
    daily_ai_message_limit: 10,
    features: ['course.access', 'ai.chat', 'game.basic', 'safe.environment'],
    show_on_landing: true,
    is_active: true,
  },
  {
    id: 'parent-plan',
    name: 'Veli Planı',
    tier: 'veli',
    price_try: 280,
    type: 'parent',
    description: 'Çocuğunuzun eğitim sürecini anlık takip edin, yapay zeka raporlarıyla gelişimini yakından gözlemleyin.',
    badge_label: 'Veli Takip',
    daily_ai_message_limit: null,
    features: ['parent.report', 'parent.progress_tracking', 'parent.weakness_alert', 'parent.multi_child', 'parent.safe_zone'],
    show_on_landing: true,
    is_active: true,
  },
  {
    id: 'pro-plan',
    name: 'Pro Plan',
    tier: 'pro',
    price_try: 350,
    type: 'individual',
    description: 'Sınırsız eğitim içerikleriyle çocuğunuzun gelişimini destekleyin, yapay zeka ile öğrenmeyi hızlandırın.',
    badge_label: 'En Popüler',
    daily_ai_message_limit: 30,
    features: ['course.access', 'ai.chat_30', 'ai.code_review', 'gamification.rewards', 'practice.quizzes'],
    show_on_landing: true,
    is_active: true,
  },
  {
    id: 'premium-plan',
    name: 'Premium Plan',
    tier: 'premium',
    price_try: 400,
    type: 'individual',
    description: 'Yapay zeka mentoru, sınırsız dersler, gelişmiş kod analizi, sertifikalar ve macera modu!',
    badge_label: 'Tam Kapsamlı',
    daily_ai_message_limit: null,
    features: ['ai.mentor', 'course.premium', 'certificate.generate', 'ai.project_generator', 'adventure.mode', 'parent.report'],
    show_on_landing: true,
    is_active: true,
  },
  {
    id: 'school-plan',
    name: 'Okul & Kurum',
    tier: 'school',
    price_try: 0,
    type: 'school',
    description: 'Okullar ve kurslar için toplu öğrenci, öğretmen ve müfredat yönetim paneli.',
    badge_label: 'Kurumsal',
    daily_ai_message_limit: null,
    features: ['school.dashboard', 'teacher.panel', 'student.management', 'report.pdf', 'unlimited.accounts'],
    show_on_landing: true,
    is_active: true,
  },
];

interface ChatMsg {
  sender: 'user' | 'mentor';
  text: string;
}

export function LandingPage() {
  // Navigation & Drawer
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Hero Interactive Slider & Auto-Play
  const [heroSlideIdx, setHeroSlideIdx] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);

  // CinAli Deep Dive State & Auto-Play
  const [activeCinAliTopic, setActiveCinAliTopic] = useState(0);
  const [cinaliPaused, setCinaliPaused] = useState(false);
  const [isSimpleMode, setIsSimpleMode] = useState(false);

  // Auto-advance Hero slides every 3.8s
  useEffect(() => {
    if (heroPaused) return;
    const timer = setInterval(() => {
      setHeroSlideIdx((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 3800);
    return () => clearInterval(timer);
  }, [heroPaused]);

  // Auto-advance CinAli topic slides every 4.2s
  useEffect(() => {
    if (cinaliPaused) return;
    const timer = setInterval(() => {
      setActiveCinAliTopic((prev) => (prev + 1) % CİNALİ_TOPICS.length);
    }, 4200);
    return () => clearInterval(timer);
  }, [cinaliPaused]);

  // AI Sandbox / Simulator State
  const [activeLang, setActiveLang] = useState<'python' | 'math' | 'physics' | 'english'>('python');
  const [isSimulating, setIsSimulating] = useState(false);
  const [simStep, setSimStep] = useState(0);
  const [heroPos, setHeroPos] = useState({ x: 0, y: 2 });
  const [collectedStar, setCollectedStar] = useState(false);
  const [physicsPos, setPhysicsPos] = useState(0);
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);

  // FAQ Accordion State
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  // Contact Form State
  const [contactForm, setContactForm] = useState({ name: '', email: '', role: 'Öğrenci / Genç Kaşif', message: '' });
  const [contactSent, setContactSent] = useState(false);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  const location = useLocation();

  // URL hash (#iletisim) veya yönlendirme ile iletişim paneline otomatik kaydırma
  useEffect(() => {
    if (location.hash === '#iletisim' || location.search.includes('school')) {
      setTimeout(() => {
        const el = document.getElementById('iletisim');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          setContactForm((prev) => ({ ...prev, role: 'Okul Yöneticisi' }));
        }
      }, 200);
    }
  }, [location.hash, location.search]);

  // Dynamic Plans from Database with reliable default fallback
  const [dbPlans, setDbPlans] = useState<Plan[]>(DEFAULT_LANDING_PLANS);
  const [expandedLandingPlans, setExpandedLandingPlans] = useState<Record<string, boolean>>({});

  useEffect(() => {
    supabase
      .from('plans')
      .select('*')
      .then(({ data, error }) => {
        if (!error && data && Array.isArray(data) && data.length > 0) {
          const visible = data.filter((p: any) => p.show_on_landing !== false && p.is_active !== false);
          if (visible.length > 0) {
            setDbPlans(visible as Plan[]);
          }
        }
      })
      .catch((err) => {
        console.warn('[LandingPage] Planlar yüklenirken varsayılan kullanılıyor:', err);
      });
  }, []);

  // Header scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 18);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll Reveal Intersection Observer with Staggering
  useEffect(() => {
    const revealEls = document.querySelectorAll(
      '.reveal, .reveal-left, .reveal-right, .reveal-scale, .section-title, .section-eyebrow, .diff-card-item, .age-branch-card, .game-perk-card, .parent-perk-card, .pricing-plan-card, .testi-card-box, .cinali-card-perk, .faq-item-card'
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -30px 0px' }
    );

    revealEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Simulator Reset when tab changes
  useEffect(() => {
    resetSimulation();
  }, [activeLang]);

  const resetSimulation = () => {
    setIsSimulating(false);
    setSimStep(0);
    setHeroPos({ x: 0, y: 2 });
    setCollectedStar(false);
    setPhysicsPos(0);
    setShowSuccess(false);

    if (activeLang === 'math') {
      setChatMessages([
        { sender: 'user', text: '3x + 5 = 20 denklemini çözmeme yardımcı olur musun?' }
      ]);
    } else if (activeLang === 'english') {
      setChatMessages([
        { sender: 'user', text: 'How are you doing today?' }
      ]);
    } else {
      setChatMessages([]);
    }
  };

  // Run Interactive Simulation
  const runSimulation = () => {
    if (isSimulating) return;
    setIsSimulating(true);
    setSimStep(1);

    if (activeLang === 'python') {
      setTimeout(() => {
        setHeroPos({ x: 1, y: 2 });
        setSimStep(2);

        setTimeout(() => {
          setHeroPos({ x: 2, y: 2 });
          setSimStep(3);

          setTimeout(() => {
            setHeroPos({ x: 3, y: 2 });
            setCollectedStar(true);
            setSimStep(4);

            setTimeout(() => {
              setShowSuccess(true);
              setIsSimulating(false);
            }, 700);
          }, 800);
        }, 800);
      }, 800);
    } else if (activeLang === 'math') {
      setTimeout(() => {
        setSimStep(2);
        setChatMessages((prev) => [
          ...prev,
          {
            sender: 'mentor',
            text: 'Harika soru! Denklemi adım adım çözelim:\n1) Her iki taraftan 5 çıkaralım:\n3x = 20 - 5  =>  3x = 15'
          }
        ]);

        setTimeout(() => {
          setSimStep(3);
          setChatMessages((prev) => [
            ...prev,
            {
              sender: 'mentor',
              text: '2) Her iki tarafı 3\'e bölelim:\nx = 15 / 3  =>  x = 5\n\nTebrikler! Çözüm tamamlandı 🎉'
            }
          ]);

          setTimeout(() => {
            setShowSuccess(true);
            setIsSimulating(false);
          }, 900);
        }, 1300);
      }, 900);
    } else if (activeLang === 'physics') {
      setTimeout(() => {
        setSimStep(2);
        let cur = 0;
        const interval = setInterval(() => {
          cur += 16;
          if (cur >= 80) {
            cur = 80;
            clearInterval(interval);
            setSimStep(3);

            setTimeout(() => {
              setShowSuccess(true);
              setIsSimulating(false);
            }, 700);
          }
          setPhysicsPos(cur);
        }, 100);
      }, 800);
    } else if (activeLang === 'english') {
      setTimeout(() => {
        setSimStep(2);
        setChatMessages((prev) => [
          ...prev,
          {
            sender: 'mentor',
            text: 'CinAli: "How are you doing today?" cümlesi tamamen doğrudur.\nAnlamı: "Bugün nasılsın?"'
          }
        ]);

        setTimeout(() => {
          setSimStep(3);
          setTimeout(() => {
            setShowSuccess(true);
            setIsSimulating(false);
          }, 700);
        }, 1100);
      }, 900);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactError(null);
    setIsSubmittingContact(true);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactForm),
      });

      if (res.ok) {
        setContactSent(true);
        setContactForm({ name: '', email: '', role: 'Öğrenci / Genç Kaşif', message: '' });
        setTimeout(() => setContactSent(false), 5000);
      } else {
        // Fallback for demo mode
        setContactSent(true);
        setContactForm({ name: '', email: '', role: 'Öğrenci / Genç Kaşif', message: '' });
        setTimeout(() => setContactSent(false), 5000);
      }
    } catch (err: any) {
      // Fallback for offline demo mode
      setContactSent(true);
      setContactForm({ name: '', email: '', role: 'Öğrenci / Genç Kaşif', message: '' });
      setTimeout(() => setContactSent(false), 5000);
    } finally {
      setIsSubmittingContact(false);
    }
  };

  return (
    <div className="landing-root">
      {/* ── TOP NAVIGATION ── */}
      <header className={`landing-nav ${scrolled ? 'scrolled' : ''}`}>
        <Link to="/" className="nav-brand">
          <MinikCoderLogo size={36} />
          <div className="nav-brand-text">
            <span>minik<span className="brand-hl">coder</span></span>
            <span className="brand-tag">ACADEMY</span>
          </div>
        </Link>

        <ul className="nav-menu">
          <li><a href="#fark">Fark Nedir?</a></li>
          <li><a href="#cinali">CinAli AI</a></li>
          <li><a href="#yas-gruplari">Yaş Grupları</a></li>
          <li><a href="#oyun">Oyunlaştırma</a></li>
          <li><a href="#veliler">Veliler İçin</a></li>
          <li><a href="#fiyatlar">Fiyatlar</a></li>
          <li><a href="#iletisim">İletişim</a></li>
        </ul>

        <div className="nav-actions">
          <Link to="/auth?mode=login" className="btn-ghost-nav">Giriş Yap</Link>
          <Link to="/auth?mode=register-student" className="btn-primary-nav">Ücretsiz Dene →</Link>
        </div>

        <button
          className="burger-btn"
          aria-label="Menüyü Aç"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Mobile Navigation Drawer */}
        <div className={`mobile-drawer ${mobileMenuOpen ? 'open' : ''}`}>
          <a href="#fark" onClick={() => setMobileMenuOpen(false)}>Fark Nedir?</a>
          <a href="#cinali" onClick={() => setMobileMenuOpen(false)}>CinAli AI</a>
          <a href="#yas-gruplari" onClick={() => setMobileMenuOpen(false)}>Yaş Grupları</a>
          <a href="#oyun" onClick={() => setMobileMenuOpen(false)}>Oyunlaştırma</a>
          <a href="#veliler" onClick={() => setMobileMenuOpen(false)}>Veliler İçin</a>
          <a href="#fiyatlar" onClick={() => setMobileMenuOpen(false)}>Fiyatlar</a>
          <a href="#iletisim" onClick={() => setMobileMenuOpen(false)}>İletişim</a>
          <div className="mobile-drawer-cta">
            <Link to="/auth?mode=login" className="btn-ghost-nav" style={{ textAlign: 'center' }} onClick={() => setMobileMenuOpen(false)}>Giriş Yap</Link>
            <Link to="/auth?mode=register-student" className="btn-primary-nav" style={{ textAlign: 'center' }} onClick={() => setMobileMenuOpen(false)}>Ücretsiz Başla</Link>
          </div>
        </div>
      </header>

      {/* ── HERO SECTION ── */}
      <section className="hero-section">
        {/* Animated WebGL Mesh Drift Shader Background */}
        <div style={{ position: 'absolute', inset: 0, opacity: 0.50, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
          <ShaderBackground className="w-full h-full" />
        </div>
        <div className="hero-grid-bg"></div>
        <div className="glow-orb glow-orb-1"></div>
        <div className="glow-orb glow-orb-2"></div>
        <div className="glow-orb glow-orb-3"></div>

        <div className="hero-layout">
          <div className="hero-left-content">
            <div className="hero-badge-pill">
              <span className="pulse-dot"></span>
              Şu an 2.400+ çocuk öğreniyor
            </div>

            <h1 className="hero-main-title">
              Çocuğunuz<br />
              <span className="accent-teal">Görsel Düşünür,</span><br />
              AI Anlatır, O<br />
              <span className="accent-glow">Öğrenir.</span>
            </h1>

            <p className="hero-desc">
              MinikCoder'da yapay zeka öğretmeni <strong>CinAli</strong>, her konuyu çizimler ve animasyonlarla anlatır. Çocuğunuz izler, anlar, sorar — <strong>oyun gibi öğrenir.</strong>
            </p>

            <div className="hero-btn-group">
              <Link to="/auth?mode=register-student" className="btn-hero-primary">
                🚀 Ücretsiz Başla
              </Link>
              <a href="#cinali" className="btn-hero-secondary">
                ▶ CinAli'yi Gör
              </a>
              <a href="#iletisim" className="btn-hero-secondary">
                🏫 Okullar İçin Demo
              </a>
            </div>

            <div className="hero-stats-row">
              <div className="hero-stat-box">
                <div className="hero-stat-val">50<span>+</span></div>
                <div className="hero-stat-label">Aktif Ders</div>
              </div>
              <div className="hero-stat-box">
                <div className="hero-stat-val">7–<span>17</span></div>
                <div className="hero-stat-label">Yaş Grubu</div>
              </div>
              <div className="hero-stat-box">
                <div className="hero-stat-val">4.9<span>★</span></div>
                <div className="hero-stat-label">Veli Memnuniyeti</div>
              </div>
            </div>
          </div>

          {/* 👧 Çocuğunuz Görsel Düşünür Yazısı ile Dersler Arasındaki Şeffaf (Filigran) Öğrenci */}
          <div className="hero-center-mascot">
            <img
              src={new URL('../assets/ogrenci-filigran-sag.png', import.meta.url).href}
              alt="MinikCoder öğrencisi interaktif dersleri işaret ediyor"
              className="hero-girl-filigran"
              loading="eager"
            />
          </div>

          {/* HERO VISUAL RIGHT — Canlı Ders Simülatörü + Üstte Yüzen Kartlar */}
          <div className="hero-visual-container">
            {/* Üstte Yüzen Kartlar */}
            <div className="floating-card-badge card-pos-robot">
              <span className="badge-icon-emoji">🤖</span>
              <div className="badge-top-tag">AI ÖĞRETMEN</div>
              <div className="badge-value-hl">CinAli</div>
              <div className="badge-sub-info">Seninle her an</div>
            </div>

            <div className="floating-card-badge card-pos-xp">
              <span className="badge-icon-emoji">⚡</span>
              <div className="badge-top-tag">BU HAFTA</div>
              <div className="badge-value-hl" style={{ color: 'var(--teal)' }}>+340 XP</div>
              <div className="badge-sub-info">Harika gidiyorsun!</div>
            </div>

            {/* Central Interactive Screen Mockup with Auto-Play */}
            <div
              className="hero-interactive-screen"
              onMouseEnter={() => setHeroPaused(true)}
              onMouseLeave={() => setHeroPaused(false)}
            >
              <div className="screen-header-bar">
                <div className="traffic-dots">
                  <span className="t-dot red"></span>
                  <span className="t-dot yellow"></span>
                  <span className="t-dot green"></span>
                </div>
                <span className="screen-badge-live">● CANLI AKIŞ</span>
              </div>

              <div className="screen-inner-content">
                <div key={heroSlideIdx} className="slide-animated-content">
                  <div className="screen-slide-header">{HERO_SLIDES[heroSlideIdx].tag}</div>

                  <div className="screen-svg-stage">
                    {/* 1. Python Kodlama SVG */}
                    {HERO_SLIDES[heroSlideIdx].svgType === 'python' && (
                      <svg viewBox="0 0 260 130" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                        <rect x="18" y="18" width="224" height="94" rx="12" fill="#0D1F1A" />
                        {/* Terminal dots */}
                        <circle cx="34" cy="32" r="3.5" fill="#FF5F57" />
                        <circle cx="46" cy="32" r="3.5" fill="#FFBD2E" />
                        <circle cx="58" cy="32" r="3.5" fill="#28C840" />
                        {/* Code lines */}
                        <text x="32" y="54" fill="#8B5CF6" fontSize="9.5" fontFamily="monospace" fontWeight="700">for</text>
                        <text x="56" y="54" fill="#38BDF8" fontSize="9.5" fontFamily="monospace">adim</text>
                        <text x="82" y="54" fill="#8B5CF6" fontSize="9.5" fontFamily="monospace" fontWeight="700">in</text>
                        <text x="98" y="54" fill="#FCD34D" fontSize="9.5" fontFamily="monospace">range(3):</text>
                        <text x="48" y="72" fill="#17C79A" fontSize="9" fontFamily="monospace">robot.adim_at(1)</text>
                        <text x="48" y="88" fill="#FACC15" fontSize="9" fontFamily="monospace">yildiz.topla() ⭐</text>
                        {/* Mini Bot badge */}
                        <rect x="190" y="44" width="38" height="38" rx="8" fill="rgba(23,199,154,0.2)" stroke="#17C79A" strokeWidth="1.5" />
                        <text x="209" y="68" fontSize="18" textAnchor="middle">🤖</text>
                        <text x="130" y="122" fill="#17C79A" fontSize="8" textAnchor="middle" fontWeight="800">Python 3 • Otomatik Döngü</text>
                      </svg>
                    )}

                    {/* 2. Görsel Matematik & Pisagor SVG */}
                    {HERO_SLIDES[heroSlideIdx].svgType === 'math' && (
                      <svg viewBox="0 0 260 130" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                        {/* Dik Üçgen */}
                        <polygon points="70,95 180,95 70,35" fill="rgba(23,199,154,0.18)" stroke="#17C79A" strokeWidth="2.5" />
                        <rect x="70" y="83" width="12" height="12" fill="none" stroke="#17C79A" strokeWidth="1.5" />
                        {/* Dik Kenar Kareleri */}
                        <rect x="32" y="35" width="38" height="60" rx="4" fill="rgba(217,70,239,0.18)" stroke="#D946EF" strokeWidth="1.5" strokeDasharray="3 2" />
                        <text x="51" y="70" fill="#D946EF" fontSize="9.5" fontWeight="900" textAnchor="middle">b²=9</text>
                        <rect x="70" y="95" width="110" height="24" rx="4" fill="rgba(6,182,212,0.18)" stroke="#06B6D4" strokeWidth="1.5" strokeDasharray="3 2" />
                        <text x="125" y="111" fill="#06B6D4" fontSize="9.5" fontWeight="900" textAnchor="middle">a²=16</text>
                        {/* Hipotenüs etiketi */}
                        <text x="138" y="55" fill="#FACC15" fontSize="11" fontWeight="900" textAnchor="middle">c² = 25 ⭐</text>
                        <text x="215" y="45" fill="#0D1F1A" fontSize="8.5" fontWeight="800">a² + b² = c²</text>
                      </svg>
                    )}

                    {/* 3. Fen Bilimleri & Güneş Sistemi SVG */}
                    {HERO_SLIDES[heroSlideIdx].svgType === 'planet' && (
                      <svg viewBox="0 0 260 130" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                        <defs>
                          <radialGradient id="sunG2" cx="50%" cy="50%" r="50%">
                            <stop offset="0%" stopColor="#FCD34D" />
                            <stop offset="100%" stopColor="#F59E0B" />
                          </radialGradient>
                          <radialGradient id="earthG2" cx="40%" cy="40%" r="60%">
                            <stop offset="0%" stopColor="#34D399" />
                            <stop offset="100%" stopColor="#065F46" />
                          </radialGradient>
                        </defs>
                        <circle cx="45" cy="65" r="26" fill="url(#sunG2)" />
                        <circle cx="45" cy="65" r="34" fill="none" stroke="#FCD34D" strokeWidth="0.8" opacity="0.4" />
                        <ellipse cx="160" cy="65" rx="105" ry="42" fill="none" stroke="rgba(6,182,212,0.4)" strokeWidth="1.4" strokeDasharray="4 3" />
                        {/* Dünya */}
                        <circle cx="235" cy="45" r="15" fill="url(#earthG2)" />
                        <circle cx="231" cy="41" r="5" fill="#1D4ED8" opacity="0.8" />
                        {/* Çekim oku */}
                        <path d="M 75 58 Q 140 30 215 45" fill="none" stroke="#06B6D4" strokeWidth="1.8" strokeDasharray="5 3" />
                        <text x="130" y="104" fill="#17C79A" fontSize="9.5" textAnchor="middle" fontWeight="800">Dünya'nın Eliptik Yörüngesi</text>
                      </svg>
                    )}

                    {/* 4. Fizik — Serbest Düşüş & Hız SVG */}
                    {HERO_SLIDES[heroSlideIdx].svgType === 'physics' && (
                      <svg viewBox="0 0 260 130" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                        {/* Kule & Yükseklik */}
                        <rect x="25" y="25" width="22" height="85" fill="#CBD5E1" stroke="#94A3B8" strokeWidth="1.5" />
                        {/* Serbest Düşen Top */}
                        <circle cx="75" cy="35" r="8" fill="#EF4444" opacity="0.5" />
                        <circle cx="75" cy="62" r="10" fill="#EF4444" opacity="0.75" />
                        <circle cx="75" cy="95" r="13" fill="#EF4444" />
                        {/* Hız oku */}
                        <line x1="75" y1="25" x2="75" y2="108" stroke="#EF4444" strokeWidth="1.5" strokeDasharray="3 2" />
                        <polygon points="75,115 71,105 79,105" fill="#EF4444" />
                        {/* Formül & Grafik kutusu */}
                        <rect x="115" y="24" width="130" height="84" rx="10" fill="#FFFFFF" stroke="rgba(23,199,154,0.3)" strokeWidth="1.5" />
                        <text x="125" y="44" fill="#0D2E26" fontSize="9" fontWeight="800">Yerçekimi: g = 9.8 m/s²</text>
                        <text x="125" y="62" fill="#0284C7" fontSize="8.5" fontWeight="700">Hız: v(t) = g · t</text>
                        <text x="125" y="78" fill="#8B5CF6" fontSize="8.5" fontWeight="700">Mesafe: h = ½ · g · t²</text>
                        <text x="125" y="96" fill="#10B981" fontSize="9" fontWeight="800">t=3s ➔ v = 29.4 m/s</text>
                      </svg>
                    )}

                    {/* 5. Lise Matematiği — Parabol & Fonksiyonlar SVG */}
                    {HERO_SLIDES[heroSlideIdx].svgType === 'parabola' && (
                      <svg viewBox="0 0 260 130" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                        {/* Koordinat Eksenleri */}
                        <line x1="20" y1="75" x2="240" y2="75" stroke="#94A3B8" strokeWidth="1.5" />
                        <polygon points="244,75 236,71 236,79" fill="#94A3B8" />
                        <text x="238" y="68" fill="#64748B" fontSize="8.5" fontWeight="800">x</text>
                        <line x1="120" y1="120" x2="120" y2="12" stroke="#94A3B8" strokeWidth="1.5" />
                        <polygon points="120,8 116,16 124,16" fill="#94A3B8" />
                        <text x="126" y="16" fill="#64748B" fontSize="8.5" fontWeight="800">y</text>
                        {/* Parabol Eğrisi: f(x) = x^2 - 2x - 3 */}
                        <path d="M 45 20 Q 140 145 235 20" fill="none" stroke="#17C79A" strokeWidth="3" />
                        {/* Tepe Noktası T(r, k) */}
                        <circle cx="140" cy="102" r="5" fill="#FACC15" stroke="#B45309" strokeWidth="1.5" />
                        <text x="140" y="118" fill="#B45309" fontSize="8.5" fontWeight="900" textAnchor="middle">Tepe T(r, k)</text>
                        {/* Kök Noktaları */}
                        <circle cx="78" cy="75" r="3.5" fill="#06B6D4" />
                        <circle cx="202" cy="75" r="3.5" fill="#06B6D4" />
                        <text x="65" y="68" fill="#06B6D4" fontSize="8" fontWeight="800">x₁ = -1</text>
                        <text x="210" y="68" fill="#06B6D4" fontSize="8" fontWeight="800">x₂ = 3</text>
                        <text x="45" y="32" fill="#17C79A" fontSize="9" fontWeight="900">f(x) = ax² + bx + c</text>
                      </svg>
                    )}
                  </div>

                  <div className="screen-explanation-box">
                    <div className="screen-explanation-title">{HERO_SLIDES[heroSlideIdx].title}</div>
                    <div className="screen-explanation-text">{HERO_SLIDES[heroSlideIdx].desc}</div>
                  </div>
                </div>
              </div>

              <div className="screen-bottom-nav">
                <button
                  className="screen-nav-button"
                  onClick={() => setHeroSlideIdx((prev) => (prev > 0 ? prev - 1 : HERO_SLIDES.length - 1))}
                >
                  ← Önceki
                </button>
                <div className="screen-dots-indicator">
                  {HERO_SLIDES.map((_, i) => (
                    <span
                      key={i}
                      className={`screen-dot-step ${heroSlideIdx === i ? 'active' : ''}`}
                      onClick={() => setHeroSlideIdx(i)}
                    />
                  ))}
                </div>
                <button
                  className="screen-nav-button active"
                  onClick={() => setHeroSlideIdx((prev) => (prev < HERO_SLIDES.length - 1 ? prev + 1 : 0))}
                >
                  Sonraki →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MARQUEE STRIP (Derslerimiz) ── */}
      <div className="marquee-section-container" id="dersler">
        <div className="marquee-header-bar">
          <div className="marquee-label-badge">
            <span className="marquee-badge-dot"></span>
            <span className="marquee-badge-icon">⚡</span>
            <span>DERSLERİMİZ</span>
          </div>
        </div>
        <div className="marquee-strip">
          <div className="marquee-track">
            {[
              { name: 'Python Kodlama', icon: '🐍' },
              { name: 'Scratch ile Oyun Geliştirme', icon: '🎮' },
              { name: 'Yapay Zeka & Prompt Mühendisliği', icon: '🤖' },
              { name: 'Fen Bilimleri & Astronomi', icon: '🔭' },
              { name: 'Görsel Matematik & Geometri', icon: '📐' },
              { name: 'Robotik Düşünce & Mantık', icon: '⚙️' },
              { name: 'Algoritma Tasarımı', icon: '💡' },
              { name: 'Veri Bilimine Giriş', icon: '📊' },
              { name: 'Web Geliştirme Temelleri', icon: '🌐' },
              { name: 'İngilizce Konuşma Mentoru', icon: '🗣️' },
              { name: 'LGS & Sınav Soru Çözümleri', icon: '📝' },
              { name: 'Siber Güvenlik Farkındalığı', icon: '🛡️' }
            ].concat([
              { name: 'Python Kodlama', icon: '🐍' },
              { name: 'Scratch ile Oyun Geliştirme', icon: '🎮' },
              { name: 'Yapay Zeka & Prompt Mühendisliği', icon: '🤖' },
              { name: 'Fen Bilimleri & Astronomi', icon: '🔭' },
              { name: 'Görsel Matematik & Geometri', icon: '📐' },
              { name: 'Robotik Düşünce & Mantık', icon: '⚙️' },
              { name: 'Algoritma Tasarımı', icon: '💡' },
              { name: 'Veri Bilimine Giriş', icon: '📊' },
              { name: 'Web Geliştirme Temelleri', icon: '🌐' },
              { name: 'İngilizce Konuşma Mentoru', icon: '🗣️' },
              { name: 'LGS & Sınav Soru Çözümleri', icon: '📝' },
              { name: 'Siber Güvenlik Farkındalığı', icon: '🛡️' }
            ]).map((item, idx) => (
              <div key={idx} className="marquee-item-tag">
                <span className="marquee-item-icon">{item.icon}</span>
                <span className="marquee-item-name">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── WHAT MAKES US DIFFERENT ── */}
      <section className="what-diff-section" id="fark">
        <div className="section-inner">
          <div className="section-eyebrow">Fark Nedir?</div>
          <h2 className="section-title">
            Diğer platformlar anlatır,<br />
            MinikCoder <span className="accent-teal">gösterir.</span>
          </h2>

          <div className="what-diff-grid">
            {/* 4 Modern Core Pillars Grid */}
            <div className="diff-cards-grid reveal">
              {/* Pillar 1: Kişiselleştirilmiş Birebir Eğitim */}
              <div className="diff-card-modern card-theme-teal">
                <div className="dcm-header">
                  <div className="dcm-icon-box">🎯</div>
                  <span className="dcm-badge">BİREBİR EĞİTİM</span>
                </div>
                <h3 className="dcm-title">Kişiselleştirilmiş Birebir Dersler</h3>
                <p className="dcm-desc">
                  7–17 yaş seviyesine ve öğrenme hızına uyarlanan, her konuyu çocuğa özel anlatan akıllı müfredat.
                </p>
                <div className="dcm-chips">
                  <span className="dcm-chip">✓ Yaşa Özel Dil</span>
                  <span className="dcm-chip">✓ Kendi Hızında</span>
                </div>
              </div>

              {/* Pillar 2: AI Hoca & Sesli-Görsel Anlatım */}
              <div className="diff-card-modern card-theme-cyan">
                <div className="dcm-header">
                  <div className="dcm-icon-box">🤖</div>
                  <span className="dcm-badge">CİNALİ AI</span>
                </div>
                <h3 className="dcm-title">Sesli, Yazılı & Çizimli Konu Anlatımı</h3>
                <p className="dcm-desc">
                  CinAli konuyu animasyonlu çizimlerle seslendirir; takıldığı an "Daha Basit Anlat" ile sadeleştirir.
                </p>
                <div className="dcm-chips">
                  <span className="dcm-chip">✓ Canlı SVG Çizim</span>
                  <span className="dcm-chip">✓ 💡 Hikaye Modu</span>
                </div>
              </div>

              {/* Pillar 3: 7/24 AI Mentor & Projeler */}
              <div className="diff-card-modern card-theme-amber">
                <div className="dcm-header">
                  <div className="dcm-icon-box">⚡</div>
                  <span className="dcm-badge">7/24 MENTOR</span>
                </div>
                <h3 className="dcm-title">Sınırsız Soru & Canlı Projeler</h3>
                <p className="dcm-desc">
                  Takıldığı her an sınırsız soru sorma, anında kod incelemesi ve mini oyun/quizlerle kalıcı pratik.
                </p>
                <div className="dcm-chips">
                  <span className="dcm-chip">✓ Anlık Kod Testi</span>
                  <span className="dcm-chip">✓ Mini Quiz & Oyun</span>
                </div>
              </div>

              {/* Pillar 4: Veli Portalı & Raporlama */}
              <div className="diff-card-modern card-theme-purple">
                <div className="dcm-header">
                  <div className="dcm-icon-box">📊</div>
                  <span className="dcm-badge">ŞEFFAF TAKİP</span>
                </div>
                <h3 className="dcm-title">Haftalık & Aylık Veli Raporları</h3>
                <p className="dcm-desc">
                  Çocuğunuzun tamamladığı projeleri, kod satırlarını, çalışma sürelerini ve beceri karnesini anlık izleyin.
                </p>
                <div className="dcm-chips">
                  <span className="dcm-chip">✓ Proje İnceleme</span>
                  <span className="dcm-chip">✓ Beceri Karnesi</span>
                </div>
              </div>
            </div>

            {/* Live Dashboard Mockup */}
            <div className="dashboard-preview-card reveal">
              <div className="dash-topbar-mock">
                <div className="dash-logo-text" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <MinikCoderLogo size={20} />
                  <span>minik<span style={{ color: 'var(--teal)' }}>coder</span></span>
                </div>
                <div className="dash-nav-links-row">
                  <span className="dash-nav-link-item">🗺️ Harita</span>
                  <span className="dash-nav-link-item">📚 Dersler</span>
                  <span className="dash-nav-link-item">🏆 Sıralama</span>
                </div>
                <div className="dash-badge-chips-row">
                  <span className="dash-chip chip-streak-fire">🔥 8 Gün</span>
                  <span className="dash-chip chip-star-count">⭐ 42</span>
                  <span className="dash-chip chip-xp-pts">340 XP</span>
                </div>
              </div>

              <div className="dash-body-layout">
                <div className="dash-sidebar-col">
                  <div className="dash-user-welcome">Merhaba Ege! 👋</div>
                  <div className="dash-user-sub">Öğrenmeye hazır mısın?</div>
                  <div className="dash-sidebar-item active">📊 Dashboard</div>
                  <div className="dash-sidebar-item">🗺️ Öğrenme Haritası</div>
                  <div className="dash-sidebar-item">📖 Derslerim</div>
                  <div className="dash-sidebar-item">🤖 AI Mentor</div>
                  <div className="dash-sidebar-group-title">OYUNLAŞTIRMA</div>
                  <div className="dash-sidebar-item">🎮 Oyun Merkezi</div>
                  <div className="dash-sidebar-item">🏆 Liderlik</div>
                  <div className="dash-sidebar-item">🏅 Rozetlerim</div>
                  <div className="dash-sidebar-item">🪙 Coin Mağaza</div>
                </div>

                <div className="dash-content-col">
                  <div className="dash-stat-cards-grid">
                    <div className="dash-mini-stat">
                      <div className="dms-label">Günlük Görev</div>
                      <div className="dms-val" style={{ color: 'var(--teal)' }}>3/3 ✓</div>
                    </div>
                    <div className="dash-mini-stat">
                      <div className="dms-label">Rozetlerim</div>
                      <div className="dms-val">🏅 12</div>
                    </div>
                    <div className="dash-mini-stat">
                      <div className="dms-label">Haftalık XP</div>
                      <div className="dms-val" style={{ color: '#EAB308' }}>892</div>
                    </div>
                  </div>

                  <div className="dash-lesson-item-card">
                    <div className="dlic-icon-wrap">🐍</div>
                    <div className="dlic-details">
                      <div className="dlic-title">Python — Döngüler</div>
                      <div className="dlic-sub">Derslere Devam Et • %60</div>
                      <div className="dlic-progress-track">
                        <div className="dlic-progress-bar" style={{ width: '60%' }}></div>
                      </div>
                    </div>
                    <button className="dlic-action-btn">Devam →</button>
                  </div>

                  <div className="dash-lesson-item-card">
                    <div className="dlic-icon-wrap" style={{ background: 'var(--cyan-light)' }}>🔬</div>
                    <div className="dlic-details">
                      <div className="dlic-title">Fen — Gezegenler & Yerçekimi</div>
                      <div className="dlic-sub">Tamamlandı • %100</div>
                      <div className="dlic-progress-track">
                        <div className="dlic-progress-bar" style={{ width: '100%', background: 'var(--cyan)' }}></div>
                      </div>
                    </div>
                    <button className="dlic-action-btn" style={{ color: 'var(--cyan)' }}>İzle →</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CİNALİ — AI ÖĞRETMEN SECTION ── */}
      <section className="cinali-deep-section" id="cinali">
        <div className="section-inner">
          <div className="cinali-deep-grid">
            {/* Left Interactive CinAli Player with Auto-Play */}
            <div
              className="cinali-player-mock reveal"
              onMouseEnter={() => setCinaliPaused(true)}
              onMouseLeave={() => setCinaliPaused(false)}
            >
              <div className="cpm-topbar">
                <div className="cpm-topbar-title">
                  <span key={activeCinAliTopic} className="cpm-topbar-title-anim">
                    ● {CİNALİ_TOPICS[activeCinAliTopic].tag} — {CİNALİ_TOPICS[activeCinAliTopic].title}
                  </span>
                </div>
                <button
                  className="cpm-simplify-btn"
                  onClick={() => setIsSimpleMode(!isSimpleMode)}
                >
                  {isSimpleMode ? '✨ Standart Anlatım' : '💡 Daha Basit Anlat'}
                </button>
              </div>

              <div className="cpm-stage-split">
                {/* CSS Animated Robot Panel */}
                <div className="cpm-robot-sidebar">
                  <div className="robot-head-wrap">
                    <div className="robot-antenna"></div>
                    <div className="robot-face-box">
                      <div className="robot-eye-led"></div>
                      <div className="robot-eye-led"></div>
                    </div>
                  </div>

                  <div className="robot-body-box">
                    <div className="robot-beating-heart">♥</div>
                  </div>

                  <div className="soundwave-bars">
                    <div className="sw-bar"></div>
                    <div className="sw-bar"></div>
                    <div className="sw-bar"></div>
                    <div className="sw-bar"></div>
                    <div className="sw-bar"></div>
                  </div>

                  <div className="robot-tag-pill">
                    CİNALİ<br />AI HOCA
                  </div>
                </div>

                {/* Dynamic SVG & Speech Area with Animated Transition */}
                <div className="cpm-slide-container">
                  <div key={activeCinAliTopic} className="slide-animated-content">
                    <div className="cpm-svg-canvas">
                      {/* 1. Python Kodlama SVG */}
                      {activeCinAliTopic === 0 && (
                        <svg viewBox="0 0 230 110" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                          <rect x="18" y="15" width="194" height="80" rx="10" fill="#0D1F1A" />
                          <text x="30" y="36" fill="#8B5CF6" fontSize="9" fontFamily="monospace" fontWeight="700">for</text>
                          <text x="52" y="36" fill="#38BDF8" fontSize="9" fontFamily="monospace">i in range(3):</text>
                          <text x="42" y="52" fill="#17C79A" fontSize="8.5" fontFamily="monospace">if engel_var():</text>
                          <text x="56" y="66" fill="#D946EF" fontSize="8.5" fontFamily="monospace">robot.zipla()</text>
                          <text x="42" y="80" fill="#FACC15" fontSize="8.5" fontFamily="monospace">robot.adim_at()</text>
                          <rect x="165" y="30" width="34" height="34" rx="8" fill="rgba(23,199,154,0.2)" stroke="#17C79A" strokeWidth="1" />
                          <text x="182" y="52" fontSize="16" textAnchor="middle">🐍</text>
                        </svg>
                      )}

                      {/* 2. Görsel Matematik & Pisagor SVG */}
                      {activeCinAliTopic === 1 && (
                        <svg viewBox="0 0 230 110" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                          <polygon points="60,82 155,82 60,32" fill="rgba(23,199,154,0.18)" stroke="#17C79A" strokeWidth="2" />
                          <rect x="60" y="72" width="10" height="10" fill="none" stroke="#17C79A" strokeWidth="1.2" />
                          {/* Kenar alan kareleri */}
                          <rect x="28" y="32" width="32" height="50" rx="3" fill="rgba(217,70,239,0.18)" stroke="#D946EF" strokeWidth="1.2" />
                          <text x="44" y="60" fill="#D946EF" fontSize="8" fontWeight="800" textAnchor="middle">9</text>
                          <rect x="60" y="82" width="95" height="20" rx="3" fill="rgba(6,182,212,0.18)" stroke="#06B6D4" strokeWidth="1.2" />
                          <text x="108" y="96" fill="#06B6D4" fontSize="8.5" fontWeight="800" textAnchor="middle">16</text>
                          <text x="120" y="48" fill="#FACC15" fontSize="10" fontWeight="900">25 (c²)</text>
                          <text x="175" y="40" fill="#0D2E26" fontSize="8" fontWeight="800">a² + b² = c²</text>
                        </svg>
                      )}

                      {/* 3. Fen Bilimleri & Güneş Sistemi SVG */}
                      {activeCinAliTopic === 2 && (
                        <svg viewBox="0 0 230 110" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                          <circle cx="35" cy="55" r="22" fill="#F59E0B" opacity="0.95" />
                          <ellipse cx="120" cy="55" rx="85" ry="34" fill="none" stroke="rgba(6,182,212,0.4)" strokeWidth="1" strokeDasharray="3 2" />
                          <circle cx="180" cy="32" r="13" fill="#10B981" />
                          <circle cx="175" cy="28" r="5" fill="#1D4ED8" opacity="0.7" />
                          <path d="M 55 48 Q 110 20 170 32" fill="none" stroke="#06B6D4" strokeWidth="1.5" />
                          <text x="115" y="96" fill="#17C79A" fontSize="8.5" textAnchor="middle" fontWeight="800">Güneş Sistemi & Gezegenler</text>
                        </svg>
                      )}

                      {/* 4. Fizik — Newton Yasası (F = m·a) SVG */}
                      {activeCinAliTopic === 3 && (
                        <svg viewBox="0 0 230 110" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                          {/* Zemin */}
                          <line x1="20" y1="80" x2="210" y2="80" stroke="#CBD5E1" strokeWidth="2" />
                          {/* Kütle Kutusu (m) */}
                          <rect x="75" y="38" width="60" height="38" rx="6" fill="#38BDF8" stroke="#0284C7" strokeWidth="1.5" />
                          <text x="105" y="62" fill="#FFFFFF" fontSize="11" fontWeight="900" textAnchor="middle">m = 5 kg</text>
                          {/* Tekerlekler */}
                          <circle cx="88" cy="80" r="5" fill="#0F172A" />
                          <circle cx="122" cy="80" r="5" fill="#0F172A" />
                          {/* Kuvvet Oku F */}
                          <path d="M 30 57 L 70 57" stroke="#EF4444" strokeWidth="2.5" />
                          <polygon points="75,57 67,52 67,62" fill="#EF4444" />
                          <text x="45" y="48" fill="#EF4444" fontSize="9.5" fontWeight="900">F = 20 N</text>
                          {/* İvme Çıktısı */}
                          <text x="145" y="52" fill="#10B981" fontSize="9" fontWeight="800">a = F / m</text>
                          <text x="145" y="68" fill="#10B981" fontSize="9.5" fontWeight="900">a = 4 m/s²</text>
                        </svg>
                      )}

                      {/* 5. Lise Matematiği — Parabol & Fonksiyonlar SVG */}
                      {activeCinAliTopic === 4 && (
                        <svg viewBox="0 0 230 110" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                          {/* Eksenler */}
                          <line x1="20" y1="65" x2="210" y2="65" stroke="#94A3B8" strokeWidth="1.2" />
                          <polygon points="214,65 207,62 207,68" fill="#94A3B8" />
                          <line x1="110" y1="100" x2="110" y2="12" stroke="#94A3B8" strokeWidth="1.2" />
                          <polygon points="110,8 107,15 113,15" fill="#94A3B8" />
                          {/* Parabol Eğrisi */}
                          <path d="M 45 22 Q 125 115 205 22" fill="none" stroke="#17C79A" strokeWidth="2.5" />
                          {/* Tepe Noktası T(r, k) */}
                          <circle cx="125" cy="85" r="4.5" fill="#FACC15" stroke="#B45309" strokeWidth="1.2" />
                          <text x="125" y="98" fill="#B45309" fontSize="8" fontWeight="900" textAnchor="middle">Tepe T(r, k)</text>
                          {/* Kökler */}
                          <circle cx="72" cy="65" r="3" fill="#06B6D4" />
                          <circle cx="178" cy="65" r="3" fill="#06B6D4" />
                          <text x="62" y="60" fill="#06B6D4" fontSize="7.5" fontWeight="800">x₁</text>
                          <text x="182" y="60" fill="#06B6D4" fontSize="7.5" fontWeight="800">x₂</text>
                          <text x="40" y="28" fill="#17C79A" fontSize="8" fontWeight="800">f(x) = ax² + bx + c</text>
                        </svg>
                      )}
                    </div>

                    <div className="cpm-speech-bubble">
                      <div className="cpm-bubble-title">
                        {isSimpleMode ? '✨ Basitleştirilmiş Anlatım (Hikaye Modu):' : CİNALİ_TOPICS[activeCinAliTopic].title}
                      </div>
                      <div className="cpm-bubble-text">
                        {isSimpleMode
                          ? CİNALİ_TOPICS[activeCinAliTopic].simpleText
                          : CİNALİ_TOPICS[activeCinAliTopic].normalText}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="cpm-bottom-footer">
                <button
                  className="cpm-nav-btn"
                  onClick={() => setActiveCinAliTopic((prev) => (prev > 0 ? prev - 1 : CİNALİ_TOPICS.length - 1))}
                >
                  ← Önceki Konu
                </button>
                <div className="cpm-dots-row">
                  {CİNALİ_TOPICS.map((_, i) => (
                    <span
                      key={i}
                      className={`cpm-dot-indicator ${activeCinAliTopic === i ? 'active' : ''}`}
                      onClick={() => setActiveCinAliTopic(i)}
                    />
                  ))}
                </div>
                <button
                  className="cpm-nav-btn primary"
                  onClick={() => setActiveCinAliTopic((prev) => (prev < CİNALİ_TOPICS.length - 1 ? prev + 1 : 0))}
                >
                  Sonraki Konu →
                </button>
              </div>
            </div>

            {/* Features Description on Right */}
            <div className="cinali-features-column reveal">
              <div className="section-eyebrow">CinAli — Kişiselleştirilmiş AI Öğretmen</div>
              <h2 className="section-title">
                Sesli, Görsel ve Yazılı<br />
                <span className="accent-teal">Birebir AI Eğitimi.</span>
              </h2>
              <p className="section-sub" style={{ marginBottom: 24 }}>
                Her öğrenciye özel üretilen dinamik çizimler, sesli anlatım ve anlık interaktif soru-cevap ile ezbersiz, kalıcı kavrama.
              </p>

              <div className="cinali-card-perk">
                <div className="ccp-icon-box icon-bg-teal">🎙️</div>
                <div className="ccp-text-box">
                  <h4>Sesli, Görsel ve Yazılı Anlatım</h4>
                  <p>CinAli tahtaya anlık çizimler yapar, sesli açıklar ve kod/formülleri yazar. 3 kanallı öğrenme ile akılda kalır.</p>
                </div>
              </div>

              <div className="cinali-card-perk">
                <div className="ccp-icon-box icon-bg-purple">💬</div>
                <div className="ccp-text-box">
                  <h4>İnteraktif Soru-Cevap & Canlı Destek</h4>
                  <p>Aklına takılan her şeyi anında sor. AI öğretmen konunun tam o saniyesindeki bağlamı bilerek sabırla yanıtlar.</p>
                </div>
              </div>

              <div className="cinali-card-perk">
                <div className="ccp-icon-box icon-bg-cyan">🎯</div>
                <div className="ccp-text-box">
                  <h4>Kişiye Özel Birebir Hız & Seviye</h4>
                  <p>Çocuğun öğrenme hızına, yaşına ve seviyesine göre ders temposunu otomatik ayarlar; geride kalma stresi yaşatmaz.</p>
                </div>
              </div>

              <div className="cinali-card-perk">
                <div className="ccp-icon-box icon-bg-amber">💡</div>
                <div className="ccp-text-box">
                  <h4>"Daha Basit Anlat" & Günlük Analojiler</h4>
                  <p>Zor gelen kavramlarda tek tıkla devreye girer; soyut konuları Lego, oyun veya hikayelerle somutlaştırarak açıklar.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INTERACTIVE AI SIMULATOR / SANDBOX ── */}
      <section className="sandbox-section" id="simulatur">
        <div className="section-inner">
          <div className="section-eyebrow centered">Canlı Deneyim</div>
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            AI Simülatörünü Hemen Dene
          </h2>
          <p className="section-sub" style={{ textAlign: 'center', margin: '0 auto' }}>
            Dersini seç, kodu veya denklemi incele, simülasyonu çalıştır ve yapay zekanın adımlarını canlı gör.
          </p>

          <div className="sandbox-workspace-card reveal">
            {/* Sidebar Subject Tabs */}
            <div className="sandbox-sidebar-menu">
              <button
                className={`sandbox-tab-btn ${activeLang === 'python' ? 'active' : ''}`}
                onClick={() => setActiveLang('python')}
              >
                <span className="tab-color-dot dot-python"></span>
                🐍 Python Kodlama
              </button>

              <button
                className={`sandbox-tab-btn ${activeLang === 'math' ? 'active' : ''}`}
                onClick={() => setActiveLang('math')}
              >
                <span className="tab-color-dot dot-math"></span>
                📐 Matematik Asistanı
              </button>

              <button
                className={`sandbox-tab-btn ${activeLang === 'physics' ? 'active' : ''}`}
                onClick={() => setActiveLang('physics')}
              >
                <span className="tab-color-dot dot-physics"></span>
                ⚙️ Fizik Simülatörü
              </button>

              <button
                className={`sandbox-tab-btn ${activeLang === 'english' ? 'active' : ''}`}
                onClick={() => setActiveLang('english')}
              >
                <span className="tab-color-dot dot-english"></span>
                🇬🇧 İngilizce Mentoru
              </button>

              <div className="sandbox-goal-box">
                <h5>🎯 Günün Görevi:</h5>
                {activeLang === 'python' && <p>Robotu döngü ile 3 adım sağa hareket ettirerek hedef yıldızı topla.</p>}
                {activeLang === 'math' && <p>3x + 5 = 20 denkleminin adım adım çözümünü AI Mentor'a sor.</p>}
                {activeLang === 'physics' && <p>Yerçekimi ivmesi (g = 9.8 m/s²) altında serbest düşüş hareketini simüle et.</p>}
                {activeLang === 'english' && <p>İngilizce bir cümlenin gramer ve telaffuz kontrolünü yapay zekaya yaptır.</p>}
              </div>
            </div>

            {/* IDE & Workspace Screen */}
            <div className="sandbox-ide-panel">
              <div className="ide-top-toolbar">
                <div className="ide-file-tab">
                  <span>📄</span>
                  {activeLang === 'python' && 'robot_adventure.py'}
                  {activeLang === 'math' && 'denklem_cozucu.math'}
                  {activeLang === 'physics' && 'yercekimi_lab.sim'}
                  {activeLang === 'english' && 'grammar_check.ai'}
                </div>

                <div className="ide-action-buttons">
                  <button
                    className="btn-ide-reset"
                    onClick={resetSimulation}
                    disabled={isSimulating}
                    style={{
                      background: '#FFFFFF',
                      color: '#0F172A',
                      border: '2px solid #0F172A',
                      fontWeight: 800,
                      cursor: isSimulating ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <RotateCcw size={13} color="#0F172A" /> Sıfırla
                  </button>
                  <button
                    className="btn-ide-run"
                    onClick={runSimulation}
                    disabled={isSimulating}
                    style={{
                      background: '#0F172A',
                      color: '#FFFFFF',
                      border: '2px solid #0F172A',
                      fontWeight: 900,
                      cursor: isSimulating ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <Play size={13} fill="#FFFFFF" color="#FFFFFF" /> {isSimulating ? 'Çalışıyor...' : 'Çalıştır ▶'}
                  </button>
                </div>
              </div>

              <div className="ide-split-workspace">
                {/* Left: Code or Equation Editor */}
                <div className="ide-code-editor-area">
                  {activeLang === 'python' && (
                    <>
                      <div className="code-line-row">
                        <span className="code-line-num">1</span>
                        <span className="code-comment"># 3 adım sağa ilerle ve yıldızı al</span>
                      </div>
                      <div className={`code-line-row ${simStep === 1 ? 'active-line' : ''}`}>
                        <span className="code-line-num">2</span>
                        <span><span className="code-keyword">for</span> i <span className="code-keyword">in</span> <span className="code-fn">range</span>(<span className="code-num">3</span>):</span>
                      </div>
                      <div className={`code-line-row ${simStep === 2 || simStep === 3 ? 'active-line' : ''}`}>
                        <span className="code-line-num">3</span>
                        <span style={{ paddingLeft: 16 }}>robot.<span className="code-fn">move_right</span>()</span>
                      </div>
                      <div className={`code-line-row ${simStep === 4 ? 'active-line' : ''}`}>
                        <span className="code-line-num">4</span>
                        <span style={{ paddingLeft: 16 }}>robot.<span className="code-fn">collect_star</span>()</span>
                      </div>
                    </>
                  )}

                  {activeLang === 'math' && (
                    <div>
                      <div style={{ fontWeight: 800, color: 'var(--cyan)', marginBottom: 8 }}>📌 Denklem Tahtası:</div>
                      <div style={{ fontSize: '1.2rem', fontFamily: 'monospace', fontWeight: 900, color: 'var(--text-title)', marginBottom: 12 }}>
                        3x + 5 = 20
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                        • Bilinmeyen: <strong>x</strong><br />
                        • Hedef: x değerini tek başına bırakmak
                      </div>
                    </div>
                  )}

                  {activeLang === 'physics' && (
                    <div>
                      <div style={{ fontWeight: 800, color: 'var(--teal-dark)', marginBottom: 8 }}>⚙️ Fizik Parametreleri:</div>
                      <div style={{ fontSize: '0.82rem', lineHeight: 1.7, color: 'var(--text-muted)' }}>
                        • Yerçekimi İvmesi: <strong>g = 9.8 m/s²</strong><br />
                        • Başlangıç Yüksekliği: <strong>h = 100 m</strong><br />
                        • Formül: <strong>y(t) = h - ½ · g · t²</strong>
                      </div>
                    </div>
                  )}

                  {activeLang === 'english' && (
                    <div>
                      <div style={{ fontWeight: 800, color: 'var(--purple)', marginBottom: 8 }}>🇬🇧 Cümle Analizi:</div>
                      <div style={{ fontSize: '1rem', fontStyle: 'italic', fontWeight: 700, color: 'var(--text-title)', marginBottom: 10 }}>
                        "How are you doing today?"
                      </div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        Gramer Yapısı: Present Continuous Tense<br />
                        Kullanım Amacı: Günlük Selamlaşma & Hal Hatır Sorma
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Live Interactive Visual Stage */}
                <div className="ide-stage-preview-area">
                  <div className="stage-title-tag">CANLI ÖNİZLEME</div>

                  {activeLang === 'python' && (
                    <div className="grid-stage-matrix">
                      {[0, 1, 2, 3].map((row) => (
                        <div key={row} className="grid-matrix-row">
                          {[0, 1, 2, 3].map((col) => {
                            const isHero = heroPos.x === col && heroPos.y === row;
                            const isStar = col === 3 && row === 2;
                            return (
                              <div
                                key={col}
                                className={`grid-matrix-cell ${isHero ? 'has-hero' : ''} ${isStar ? 'has-target' : ''}`}
                              >
                                {isHero && <Bot size={22} color="var(--teal)" />}
                                {isStar && !collectedStar && <Star size={22} color="#F59E0B" fill="#FCD34D" />}
                                {isStar && collectedStar && <CheckCircle2 size={22} color="#10B981" />}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}

                  {(activeLang === 'math' || activeLang === 'english') && (
                    <div className="sandbox-chat-flow">
                      {chatMessages.map((m, idx) => (
                        <div key={idx} className={`sandbox-chat-bubble ${m.sender}`}>
                          <div style={{ fontWeight: 800, fontSize: '0.8rem', marginBottom: 2 }}>
                            {m.sender === 'user' ? 'Öğrenci 👤:' : 'CinAli AI 🤖:'}
                          </div>
                          <div style={{ whiteSpace: 'pre-line' }}>{m.text}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {activeLang === 'physics' && (
                    <div className="physics-sky-area">
                      <div className="physics-ball-obj" style={{ top: `${physicsPos}%` }}>
                        <Activity size={16} />
                      </div>
                    </div>
                  )}

                  {/* Success Overlay */}
                  {showSuccess && (
                    <div className="success-congrats-modal">
                      <Trophy size={40} color="#F59E0B" />
                      <h3>Tebrikler! Adım Başarılı</h3>
                      <p>Simülasyon adımları başarıyla tamamlandı.</p>
                      <div className="success-xp-badge">⚡ +20 XP Kazandın!</div>
                      <Link to="/auth?mode=register-student" className="btn-hero-primary" style={{ padding: '10px 22px', fontSize: '0.88rem' }}>
                        Tüm Dersleri Aç →
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              {/* Real-time Console */}
              <div className="ide-console-output">
                <span>&gt; KONSOL:</span>
                {simStep === 0 && <span>Kodun çalıştırılması bekleniyor...</span>}
                {simStep === 1 && <span>Hesaplamalar başlatıldı...</span>}
                {simStep >= 2 && activeLang === 'python' && <span>Karakter sağa doğru ilerliyor...</span>}
                {simStep >= 2 && activeLang === 'math' && <span>Denklem adımları çözümleniyor...</span>}
                {simStep >= 2 && activeLang === 'physics' && <span>Serbest düşüş ivmesi simüle ediliyor.</span>}
                {simStep >= 2 && activeLang === 'english' && <span>Gramer ve anlam doğrulaması yapıldı.</span>}
                {collectedStar && <span>🎯 Hedef yıldız başarıyla toplandı!</span>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4-STEP HOW IT WORKS ── */}
      <section className="how-it-works-section">
        <div className="section-inner">
          <div className="section-eyebrow centered">Nasıl Çalışır?</div>
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            4 adımda öğrenme<br />
            <span className="accent-teal">macerası başlar</span>
          </h2>

          <div className="how-steps-timeline reveal">
            <div className="how-step-card">
              <div className="step-num-circle">🎯</div>
              <div className="step-card-title">1. Konu Seç</div>
              <div className="step-card-desc">Öğrenme haritasından yaşına ve ilgi alanına uygun konuyu seç.</div>
            </div>

            <div className="how-step-card">
              <div className="step-num-circle">🎨</div>
              <div className="step-card-title">2. CinAli Hazırlar</div>
              <div className="step-card-desc">AI öğretmenin o konu için özel görseller ve sesli anlatım üretir.</div>
            </div>

            <div className="how-step-card">
              <div className="step-num-circle">📖</div>
              <div className="step-card-title">3. İzle & Öğren</div>
              <div className="step-card-desc">Slaytları dinle, anlamadığında sor, kendi hızında ilerle.</div>
            </div>

            <div className="how-step-card">
              <div className="step-num-circle">🏆</div>
              <div className="step-card-title">4. Kazan & Büyü</div>
              <div className="step-card-desc">XP kazan, rozet topla, liderlik tablosuna çık ve seviye atla.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AGE PATHS ── */}
      <section className="age-paths-section" id="yas-gruplari">
        <div className="section-inner">

          {/* ── Üst: Görsel + Başlık split ── */}
          <div className="age-hero-split reveal">
            {/* Sol: Başlık ve açıklama */}
            <div className="age-hero-text">
              <div className="section-eyebrow">Yaş Grupları</div>
              <h2 className="section-title">
                Her yaşa özel<br />
                <span className="accent-teal">öğrenme yolu</span>
              </h2>
              <p className="section-sub">Küçük kaşiften genç yazılımcıya — her öğrenciye uygun müfredat ve dil.</p>
              {/* Mini istatistik bar */}
              <div className="age-hero-stats">
                <div className="ahs-item">
                  <span className="ahs-num">7–17</span>
                  <span className="ahs-lbl">Yaş Aralığı</span>
                </div>
                <div className="ahs-divider" />
                <div className="ahs-item">
                  <span className="ahs-num">50+</span>
                  <span className="ahs-lbl">Aktif Ders</span>
                </div>
                <div className="ahs-divider" />
                <div className="ahs-item">
                  <span className="ahs-num">3</span>
                  <span className="ahs-lbl">Seviye Grubu</span>
                </div>
              </div>
            </div>

            {/* Sağ: Küçük kız görsel */}
            <div className="age-hero-img-wrap">
              <div className="age-hero-img-glow" />
              <img
                src={new URL('../assets/kucuk-kiz-kodlama.jpg', import.meta.url).href}
                alt="Küçük kız Scratch ile kodlama yapıyor — MinikCoder"
                className="age-hero-img"
                loading="lazy"
              />
              {/* Floating scratch badge */}
              <div className="age-hero-float-badge">
                <span>🎮</span>
                <div>
                  <div className="ahfb-title">Scratch ile Oyun Yaptım!</div>
                  <div className="ahfb-sub">7–9 Yaş · +50 XP</div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Alt: 3 yaş kartı ── */}
          <div className="age-paths-grid reveal">
            <div className="age-tier-card card-theme-teal">
              <div className="age-tier-num">7–9 <span>yaş</span></div>
              <div className="age-tier-name">Küçük Kaşif</div>
              <div className="age-course-tags">
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--teal)' }}></span>Scratch ile Oyun Yapımı</div>
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--teal)' }}></span>Algoritmalar & Mantık</div>
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--teal)' }}></span>Fen Keşifleri & Uzay</div>
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--teal)' }}></span>Sayılar Dünyası & Görsel Matematik</div>
              </div>
              <Link to="/auth?mode=register-student" className="age-explore-cta">
                Keşfet →
              </Link>
            </div>

            <div className="age-tier-card card-theme-cyan">
              <div className="age-tier-num">10–13 <span>yaş</span></div>
              <div className="age-tier-name" style={{ color: 'var(--cyan)' }}>Genç Yapıcı</div>
              <div className="age-course-tags">
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--cyan)' }}></span>Python Temelleri & Döngüler</div>
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--cyan)' }}></span>Veri & Grafikler</div>
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--cyan)' }}></span>Robotik Düşünce & Simülasyon</div>
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--cyan)' }}></span>Matematiksel Problem Çözme</div>
              </div>
              <Link to="/auth?mode=register-student" className="age-explore-cta" style={{ color: 'var(--cyan)' }}>
                Keşfet →
              </Link>
            </div>

            <div className="age-tier-card card-theme-purple">
              <div className="age-tier-num">14–17 <span>yaş</span></div>
              <div className="age-tier-name" style={{ color: 'var(--purple)' }}>Genç Geliştirici</div>
              <div className="age-course-tags">
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--purple)' }}></span>Python İleri Seviye & Nesne Tabanlı</div>
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--purple)' }}></span>Yapay Zeka & Prompt Mühendisliği</div>
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--purple)' }}></span>Web Geliştirme & API'ler</div>
                <div className="age-tag-item"><span className="bullet-dot" style={{ background: 'var(--yellow)' }}></span>Veri Bilimi & Proje Portfolyosu</div>
              </div>
              <Link to="/auth?mode=register-student" className="age-explore-cta" style={{ color: 'var(--purple)' }}>
                Keşfet →
              </Link>
            </div>
          </div>

        </div>
      </section>


      {/* ── GAMIFICATION SECTION ── */}
      <section className="gamification-section" id="oyun">
        <div className="section-inner">
          <div className="gamification-grid">
            <div className="reveal">
              <div className="section-eyebrow">Oyunlaştırma</div>
              <h2 className="section-title">
                Öğrenmek<br />
                <span className="accent-yellow">oyun gibi</span> gelsin
              </h2>
              <p className="section-sub">
                XP'den rozetlere, lig sisteminden coin mağazasına — her tamamlanan ders anında ödüllendirilir.
              </p>

              <div className="game-perks-grid">
                <div className="game-perk-card">
                  <span className="gpc-icon">⚡</span>
                  <div className="gpc-title">XP & Seviyeler</div>
                  <div className="gpc-desc">Her tamamlanan ders ve quiz XP kazandırır, yeni seviyeler açılır.</div>
                </div>

                <div className="game-perk-card">
                  <span className="gpc-icon">🏅</span>
                  <div className="gpc-title">30+ Rozet</div>
                  <div className="gpc-desc">İlk Python kodundan usta ligine kadar başarı rozetleri topla.</div>
                </div>

                <div className="game-perk-card">
                  <span className="gpc-icon">🏆</span>
                  <div className="gpc-title">Lig & Sıralama</div>
                  <div className="gpc-desc">Haftalık lig sistemi ile arkadaşlarınla tatlı bir yarışa gir.</div>
                </div>

                <div className="game-perk-card">
                  <span className="gpc-icon">🪙</span>
                  <div className="gpc-title">Coin Mağaza</div>
                  <div className="gpc-desc">Kazandığın coinleri özel avatar, tema ve sertifikalara harca.</div>
                </div>
              </div>
            </div>

            {/* Compact Gamification Showcase with Sleek Black Frame & Real Podium */}
            <div className="game-showcase-mock reveal">
              {/* Header Bar */}
              <div className="gsm-header-bar">
                <div className="gsm-hb-title">
                  <span>🏆</span> Haftalık Lig Sıralaması
                </div>
                <div className="gsm-hb-pill">
                  ⭐ Seviye 7 · 892 XP
                </div>
              </div>

              {/* Top 3 Podium Pillars */}
              <div className="gsm-podium-section">
                <div className="podium-stage-row">
                  {/* Rank 2 - kadir Y. */}
                  <div className="podium-col">
                    <div className="podium-medal">🥈</div>
                    <div className="podium-user-name">kadir Y.</div>
                    <div className="podium-school-tag">ankara keçiören fen</div>
                    <div className="podium-xp-badge">51 XP</div>
                    <div className="podium-pillar rank-2">2</div>
                  </div>

                  {/* Rank 1 - emir Y. */}
                  <div className="podium-col">
                    <div className="podium-medal">🥇</div>
                    <div className="podium-user-name">emir Y.</div>
                    <div className="podium-school-tag">binali yıldırım kolej</div>
                    <div className="podium-xp-badge">245 XP</div>
                    <div className="podium-pillar rank-1">1</div>
                  </div>

                  {/* Rank 3 - erdo B. */}
                  <div className="podium-col">
                    <div className="podium-medal">🥉</div>
                    <div className="podium-user-name">erdo B.</div>
                    <div className="podium-school-tag">Tuna Üçer</div>
                    <div className="podium-xp-badge">50 XP</div>
                    <div className="podium-pillar rank-3">3</div>
                  </div>
                </div>
              </div>

              {/* Tam Sıralama List */}
              <div className="gsm-ranking-section">
                <div className="ranking-heading">Tam Sıralama</div>
                <div className="ranking-list-wrap">
                  {/* 1st Place */}
                  <div className="ranking-row-item">
                    <div className="rri-left">
                      <span className="rri-medal-icon">🥇</span>
                      <div className="rri-avatar purple">EY</div>
                      <div className="rri-info">
                        <div className="rri-name">emir Y.</div>
                        <div className="rri-sub">binali yıldırım kolej · Seviye 1</div>
                      </div>
                    </div>
                    <span className="rri-xp-chip">⚡ 245 XP</span>
                  </div>

                  {/* 2nd Place (User Sen) */}
                  <div className="ranking-row-item active-user-row">
                    <div className="rri-left">
                      <span className="rri-medal-icon">🥈</span>
                      <div className="rri-avatar blue">KY</div>
                      <div className="rri-info">
                        <div className="rri-name">kadir Y. <span style={{ color: '#2563EB', fontWeight: 800 }}>(Sen)</span></div>
                        <div className="rri-sub">ankara keçiören fen · Seviye 1</div>
                      </div>
                    </div>
                    <span className="rri-xp-chip">⚡ 51 XP</span>
                  </div>

                  {/* 3rd Place */}
                  <div className="ranking-row-item">
                    <div className="rri-left">
                      <span className="rri-medal-icon">🥉</span>
                      <div className="rri-avatar blue">EB</div>
                      <div className="rri-info">
                        <div className="rri-name">erdo B.</div>
                        <div className="rri-sub">Tuna Üçer · Seviye 1</div>
                      </div>
                    </div>
                    <span className="rri-xp-chip">⚡ 50 XP</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── UNIFIED PARENT PORTAL & TRUST SECTION ── */}
      <section className="parent-portal-section" id="veliler">
        <div className="section-inner">
          <div className="parent-section-header reveal">
            <div className="section-eyebrow centered">Veliler İçin & Güvenilir Eğitim</div>
            <h2 className="section-title" style={{ textAlign: 'center' }}>
              Çocuğunuzun gelişimini<br />
              <span className="accent-teal">şeffafça takip edin</span>
            </h2>
            <p className="section-sub" style={{ textAlign: 'center', margin: '0 auto 44px' }}>
              Veli paneli sayesinde çocuğunuzun ekran başında harcadığı her dakikanın nasıl bir kazanıma dönüştüğünü görün. 2.400'den fazla mutlu aile MinikCoder'a güveniyor.
            </p>
          </div>

          {/* ── Üst Bölüm: Veli Avantajları + Haftalık Rapor Kartı ── */}
          <div className="parent-portal-grid reveal">
            <div className="parent-perks-col">
              <div className="parent-perks-list">
                <div className="parent-perk-card">
                  <div className="ppc-icon" style={{ background: 'var(--teal-light)' }}>📊</div>
                  <div className="ppc-content">
                    <h4>Haftalık İlerleme Raporu</h4>
                    <p>Tamamlanan dersler, kazanılan XP ve güçlü olduğu konular her pazar veli paneline yansır.</p>
                  </div>
                </div>

                <div className="parent-perk-card">
                  <div className="ppc-icon" style={{ background: 'var(--cyan-light)' }}>💬</div>
                  <div className="ppc-content">
                    <h4>Soru & Cevap Geçmişi</h4>
                    <p>Çocuğunuzun CinAli'ye sorduğu soruları ve aldığı eğitici yanıtları inceleyebilirsiniz.</p>
                  </div>
                </div>

                <div className="parent-perk-card">
                  <div className="ppc-icon" style={{ background: 'var(--yellow-light)' }}>🔔</div>
                  <div className="ppc-content">
                    <h4>Akıllı Bildirimler</h4>
                    <p>Yeni bir rozet kazanıldığında veya seri kırılma riski olduğunda bilgilendirilirsiniz.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Weekly Report Card Mockup */}
            <div className="weekly-report-card">
              <div className="wrc-header">
                <div>
                  <div className="wrc-title">📊 Haftalık Veli Özeti</div>
                  <div className="wrc-date">11–17 Ağustos Dönemi</div>
                </div>
                <span className="wrc-status-chip">Gelişim: Mükemmel ↑</span>
              </div>

              <div className="wrc-stats-3col">
                <div className="wrc-stat-block">
                  <div className="wrc-stat-num" style={{ color: 'var(--teal-dark)' }}>7</div>
                  <div className="wrc-stat-label">Ders Tamamlandı</div>
                </div>
                <div className="wrc-stat-block">
                  <div className="wrc-stat-num" style={{ color: '#EAB308' }}>892</div>
                  <div className="wrc-stat-label">Kazanılan XP</div>
                </div>
                <div className="wrc-stat-block">
                  <div className="wrc-stat-num" style={{ color: 'var(--purple)' }}>3</div>
                  <div className="wrc-stat-label">Yeni Rozet</div>
                </div>
              </div>

              <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--text-muted)', marginBottom: 12, textTransform: 'uppercase' }}>
                Konu Başarı Yüzdeleri
              </div>

              <div className="wrc-progress-list">
                <div className="wrc-progress-item">
                  <div className="wpi-row">
                    <span className="wpi-name">🐍 Python — Döngüler ve Şartlar</span>
                    <span className="wpi-pct">%85</span>
                  </div>
                  <div className="wpi-bar-rail">
                    <div className="wpi-bar-fill" style={{ width: '85%', background: 'var(--teal)' }}></div>
                  </div>
                </div>

                <div className="wrc-progress-item">
                  <div className="wpi-row">
                    <span className="wpi-name">🔬 Fen — Güneş Sistemi & Yerçekimi</span>
                    <span className="wpi-pct" style={{ color: 'var(--cyan)' }}>%92</span>
                  </div>
                  <div className="wpi-bar-rail">
                    <div className="wpi-bar-fill" style={{ width: '92%', background: 'var(--cyan)' }}></div>
                  </div>
                </div>

                <div className="wrc-progress-item">
                  <div className="wpi-row">
                    <span className="wpi-name">📐 Matematik — Pisagor & Kümeler</span>
                    <span className="wpi-pct" style={{ color: 'var(--purple)' }}>%70</span>
                  </div>
                  <div className="wpi-bar-rail">
                    <div className="wpi-bar-fill" style={{ width: '70%', background: 'var(--purple)' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Orta Bölüm: Küçültülmüş Kompakt Veli Görseli & 7/24 YZ Kartı ── */}
          <div className="parent-compact-banner reveal">
            <div className="pcb-visual-wrap">
              <img
                src={new URL('../assets/veli-gorseli.jpg', import.meta.url).href}
                alt="Aile birlikte MinikCoder platformunda çocuğun gelişimini takip ediyor"
                className="pcb-img"
                loading="lazy"
              />
              <div className="pcb-badge">
                <span className="pcb-badge-dot"></span>
                Gerçek zamanlı veli paneli
              </div>
            </div>

            <div className="pcb-content">
              <div className="pcb-eyebrow">GÜVENİLİR PEDAGOG ONAYLI EĞİTİM</div>
              <h3 className="pcb-title">
                Çocuğunuzun yanında <span className="accent-teal">7/24 YZ öğretmen</span>
              </h3>
              <p className="pcb-desc">
                MinikCoder'da her ders, her soru ve her başarı veli paneline anında yansır.
                Çocuğunuz keşfedip öğrenirken siz de güvenle yanında olun.
              </p>

              <div className="pcb-stats-row">
                <div className="pcb-stat-item">
                  <span className="pcb-stat-num">4.9★</span>
                  <span className="pcb-stat-lbl">Veli Memnuniyeti</span>
                </div>
                <div className="pcb-stat-divider"></div>
                <div className="pcb-stat-item">
                  <span className="pcb-stat-num">2.4K+</span>
                  <span className="pcb-stat-lbl">Aktif Öğrenci</span>
                </div>
                <div className="pcb-stat-divider"></div>
                <div className="pcb-stat-item">
                  <span className="pcb-stat-num">%94</span>
                  <span className="pcb-stat-lbl">Tekrar Kullanım</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Alt Bölüm: Veli Yorum Kartları ── */}
          <div className="parent-testimonials-wrap reveal">
            <div className="testimonials-grid">
              <div className="testi-card-box">
                <div className="testi-stars-row">★★★★★</div>
                <div className="testi-quote-text">
                  "Oğlum artık 'bugün ders yok mu?' diye soruyor. CinAli'nin anlatım şekli gerçekten farklı — görseller ve sesli anlatım birlikte çok etkili."
                </div>
                <div className="testi-author-row">
                  <div className="testi-avatar-box" style={{ background: 'linear-gradient(135deg, #17C79A, #06B6D4)' }}>AY</div>
                  <div>
                    <div className="testi-name">Ayşe Y.</div>
                    <div className="testi-role">Veli · 9 yaşında çocuk</div>
                  </div>
                </div>
              </div>

              <div className="testi-card-box">
                <div className="testi-stars-row">★★★★★</div>
                <div className="testi-quote-text">
                  "Kızım matematiği hiç sevmiyordu. MinikCoder'da kesirler konusunu CinAli hikaye gibi anlattı, şimdi kendi kendine çözüyor. İnanılmaz bir değişim."
                </div>
                <div className="testi-author-row">
                  <div className="testi-avatar-box" style={{ background: 'linear-gradient(135deg, #D946EF, #9333EA)', color: 'white' }}>MK</div>
                  <div>
                    <div className="testi-name">Mehmet K.</div>
                    <div className="testi-role">Veli · 11 yaşında çocuk</div>
                  </div>
                </div>
              </div>

              <div className="testi-card-box">
                <div className="testi-stars-row">★★★★★</div>
                <div className="testi-quote-text">
                  "Haftalık veli raporu harika bir kolaylık. Çocuğumun hangi konuyu ne kadar sürede anladığını ve ne sorduğunu şeffafça görebiliyorum."
                </div>
                <div className="testi-author-row">
                  <div className="testi-avatar-box" style={{ background: 'linear-gradient(135deg, #F59E0B, #D97706)' }}>ZB</div>
                  <div>
                    <div className="testi-name">Zeynep B.</div>
                    <div className="testi-role">Veli · 14 yaşında çocuk</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING SECTION ── */}
      <section className="pricing-section" id="fiyatlar">
        <div className="section-inner">
          <div className="section-eyebrow centered">Fiyatlandırma</div>
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            Basit, şeffaf ve esnek<br />
            <span className="accent-teal">fiyatlandırma</span>
          </h2>
          <p className="section-sub" style={{ textAlign: 'center', margin: '0 auto' }}>
            İlk 7 gün tamamen ücretsiz. Kredi kartı gerekmez. İstediğin zaman iptal et.
          </p>

          <div className="pricing-scroll-container">
            <div className="pricing-cards-row">
              {dbPlans.map((plan) => {
                const isFeatured = plan.badge_label || plan.tier?.includes('pro') || plan.tier?.includes('premium') || plan.tier?.includes('standart');
                const allLandingFeats = getPlanFeatures(plan);
                const isPlanExpanded = !!expandedLandingPlans[plan.id];
                const INITIAL_LANDING_COUNT = 5;
                const visibleLandingFeats = isPlanExpanded ? allLandingFeats : allLandingFeats.slice(0, INITIAL_LANDING_COUNT);
                const remainingCount = allLandingFeats.length - INITIAL_LANDING_COUNT;

                const isSchool = plan.type === 'school' || plan.tier === 'school' || plan.tier?.toLowerCase().includes('school') || plan.tier?.toLowerCase().includes('okul') || plan.name?.toLowerCase().includes('okul');

                return (
                  <div key={plan.id} className={`pricing-plan-card ${isFeatured ? 'is-featured' : ''}`}>
                    {plan.badge_label && (
                      <span className="pricing-popular-tag">{plan.badge_label}</span>
                    )}
                    <div className="plan-tier-name">{plan.name}</div>
                    <div className="plan-price-row">
                      {isSchool ? (
                        <>
                          <span className="plan-number" style={{ fontSize: '1.85rem', color: 'var(--cyan)' }}>Özel Teklif</span>
                          <span className="plan-period">/ kurumsal anlaşmalı</span>
                        </>
                      ) : (
                        <>
                          <span className="plan-currency">₺</span>
                          <span className="plan-number">{plan.price_try}</span>
                          <span className="plan-period">{plan.price_try === 0 ? 'sonsuza kadar' : '/ ay'}</span>
                        </>
                      )}
                    </div>
                    <div className="plan-desc-text">{plan.description || 'MinikCoder Akademi Paketi'}</div>

                    <div className="plan-features-list">
                      {visibleLandingFeats.map((feat) => (
                        <div key={feat.id} className="plan-feat-row">
                          <div className="pfr-check">✓</div>
                          <div className="plan-feat-content">
                            <div className="plan-feat-title">{feat.title}</div>
                            {feat.desc && <div className="plan-feat-desc">{feat.desc}</div>}
                          </div>
                        </div>
                      ))}
                    </div>

                    {allLandingFeats.length > INITIAL_LANDING_COUNT && (
                      <button
                        type="button"
                        className="landing-pricing-expand-btn"
                        onClick={() => setExpandedLandingPlans(prev => ({ ...prev, [plan.id]: !prev[plan.id] }))}
                      >
                        <span>{isPlanExpanded ? 'Daha Az Özellik' : `Tüm Özellikler (${remainingCount}+)`}</span>
                        <span style={{ fontSize: '11px' }}>{isPlanExpanded ? '▲' : '▼'}</span>
                      </button>
                    )}

                    {isSchool ? (
                      <a
                        href="#iletisim"
                        className={`btn-plan-action ${isFeatured ? 'btn-plan-filled' : 'btn-plan-outline'}`}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById('iletisim')?.scrollIntoView({ behavior: 'smooth' });
                          setContactForm(prev => ({ ...prev, role: 'Okul Yöneticisi' }));
                        }}
                      >
                        Kurumsal İletişim & Teklif Al →
                      </a>
                    ) : (
                      <Link
                        to={plan.price_try === 0 ? '/auth?mode=register-student' : '/pricing'}
                        className={`btn-plan-action ${isFeatured ? 'btn-plan-filled' : 'btn-plan-outline'}`}
                      >
                        {plan.price_try === 0 ? 'Ücretsiz Başla' : `${plan.name}'a Geç →`}
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ SECTION ── */}
      <section className="faq-section" id="sss">
        <div className="section-inner">
          <div className="section-eyebrow centered">Sıkça Sorulan Sorular</div>
          <h2 className="section-title" style={{ textAlign: 'center' }}>
            Aklınıza takılan her şey
          </h2>
          <p className="section-sub" style={{ textAlign: 'center', margin: '0 auto' }}>
            MinikCoder hakkında en çok merak edilen sorular ve yanıtları.
          </p>

          <div className="faq-accordion-list reveal">
            {[
              {
                q: 'MinikCoder hangi ders ve yaş gruplarını kapsamaktadır?',
                a: '7–17 yaş arası öğrenciler için Python kodlama, Scratch oyun yapımı, görsel matematik, fen bilimleri, robotik düşünce, yapay zeka ve İngilizce modüllerini içerir.'
              },
              {
                q: 'CinAli yapay zeka öğretmeni çocukların güvenliğini nasıl sağlıyor?',
                a: 'CinAli, çocuk koruma filtreleri ve pedagojik eğitim kuralları ile sınırlandırılmış güvenli bir yapay zeka modelidir. Asla uygunsuz içerik üretmez ve daima teşvik edici bir dil kullanır.'
              },
              {
                q: 'Ebeveyn olarak çocuğumun gelişimini nasıl takip edebilirim?',
                a: 'Veli paneli üzerinden çalışma sürelerini, tamamlanan dersleri, kazanılan XP/rozetleri ve her pazar hazırlanan haftalık gelişim analizini görüntüleyebilirsiniz.'
              },
              {
                q: 'Okullar ve öğretmenler için sınıf paneli var mı?',
                a: 'Evet! Okul ve öğretmenler sınıflar oluşturabilir, öğrencilerine toplu ödev ve projeler atayabilir, sınıf genel başarı grafiklerini PDF veya Excel olarak indirebilir.'
              },
              {
                q: 'Platformu denemek için kredi kartı gerekiyor mu?',
                a: 'Kesinlikle hayır. Ücretsiz üye olarak 7 gün boyunca hiçbir kart bilgisi girmeden temel dersleri ve CinAli AI asistanını deneyimleyebilirsiniz.'
              }
            ].map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div key={idx} className={`faq-item-panel ${isOpen ? 'open' : ''}`}>
                  <button
                    className="faq-question-btn"
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                  >
                    <span>{faq.q}</span>
                    <span className="faq-chevron-icon">
                      <ChevronDown size={18} />
                    </span>
                  </button>
                  {isOpen && (
                    <div className="faq-answer-content">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CONTACT & DEMO SECTION ── */}
      <section className="contact-section" id="iletisim">
        <div className="section-inner">
          <div className="contact-card-box reveal">
            <div className="contact-info-col">
              <div className="section-eyebrow">İletişim & Okul Lisansı</div>
              <h3>Bizimle İletişime Geçin</h3>
              <p>
                Eğitimde yapay zeka dönüşümünü birlikte başlatalım. Sorularınız, okul demosu talepleriniz veya iş birliği fikirleriniz için her zaman buradayız.
              </p>

              <div className="contact-items-stack">
                <div className="contact-item-row">
                  <div className="contact-icon-pill">
                    <Mail size={18} />
                  </div>
                  <div className="contact-detail-text">
                    <h5>E-POSTA</h5>
                    <a href="mailto:minikcoderakademi@gmail.com">minikcoderakademi@gmail.com</a>
                  </div>
                </div>

                <div className="contact-item-row">
                  <div className="contact-icon-pill">
                    <Globe size={18} />
                  </div>
                  <div className="contact-detail-text">
                    <h5>WEB PLATFORMU</h5>
                    <span>minikcoderacademy.com</span>
                  </div>
                </div>

                <div className="contact-item-row">
                  <div className="contact-icon-pill">
                    <MapPin size={18} />
                  </div>
                  <div className="contact-detail-text">
                    <h5>HİZMET BÖLGESİ</h5>
                    <span>Türkiye Geneli & Çevrimiçi</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form-col">
              {contactSent ? (
                <div className="contact-alert-success">
                  🎉 Mesajınız başarıyla iletildi! Ekibimiz en kısa sürede sizinle iletişime geçecektir.
                </div>
              ) : (
                <form onSubmit={handleContactSubmit}>
                  <div className="form-group-field">
                    <label htmlFor="c-name">Adınız Soyadınız</label>
                    <input
                      id="c-name"
                      type="text"
                      placeholder="Örn: Selin Kaya"
                      required
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                    />
                  </div>

                  <div className="form-group-field">
                    <label htmlFor="c-email">E-posta Adresiniz</label>
                    <input
                      id="c-email"
                      type="email"
                      placeholder="Örn: selin@example.com"
                      required
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    />
                  </div>

                  <div className="form-group-field">
                    <label htmlFor="c-role">Platformdaki Rolünüz</label>
                    <select
                      id="c-role"
                      value={contactForm.role}
                      onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
                    >
                      <option value="Öğrenci / Genç Kaşif">Öğrenci / Genç Kaşif</option>
                      <option value="Veli / Ebeveyn">Veli / Ebeveyn</option>
                      <option value="Öğretmen">Öğretmen</option>
                      <option value="Okul Yöneticisi">Okul Yöneticisi</option>
                      <option value="Diğer / İş Birliği">Diğer / İş Birliği</option>
                    </select>
                  </div>

                  <div className="form-group-field">
                    <label htmlFor="c-msg">Mesajınız / Talep Notunuz</label>
                    <textarea
                      id="c-msg"
                      rows={4}
                      placeholder="Sorularınızı veya okul demo talebinizi buraya yazabilirsiniz..."
                      required
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                    />
                  </div>

                  {contactError && (
                    <div style={{ color: '#EF4444', fontSize: '0.82rem', fontWeight: 700 }}>
                      ⚠️ {contactError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn-contact-submit"
                    disabled={isSubmittingContact}
                  >
                    <Send size={16} />
                    {isSubmittingContact ? 'Gönderiliyor...' : 'Mesajı Gönder'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── HIGH-IMPACT FINAL CTA ── */}
      <section className="cta-banner-section">
        <div className="section-inner">
          <div className="cta-glow-box reveal">
            <div className="section-eyebrow centered">Başlamak İçin Bir Sebep Yeter</div>
            <h2 className="section-title">
              Çocuğunuzun gözleri<br />
              <span className="accent-teal">parlasın.</span>
            </h2>
            <p className="section-sub">
              İlk 7 gün tamamen ücretsiz. Kredi kartı gerekmez. İstediğin zaman iptal et.
            </p>

            <div className="cta-buttons-wrap">
              <Link to="/auth?mode=register-student" className="btn-hero-primary">
                🚀 Ücretsiz Başla
              </Link>
              <a href="#cinali" className="btn-hero-secondary">
                CinAli'yi Tanı →
              </a>
            </div>

            <div className="cta-trust-items-row">
              <div className="cta-trust-chip"><span className="check-icon">✓</span> 7 gün ücretsiz</div>
              <div className="cta-trust-chip"><span className="check-icon">✓</span> Kredi kartı yok</div>
              <div className="cta-trust-chip"><span className="check-icon">✓</span> İstediğin an iptal</div>
              <div className="cta-trust-chip"><span className="check-icon">✓</span> 2.400+ aktif öğrenci</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── COMPREHENSIVE FOOTER ── */}
      <footer className="landing-footer">
        <div className="footer-columns-grid">
          <div>
            <Link to="/" className="nav-brand" style={{ marginBottom: 14 }}>
              <MinikCoderLogo size={40} />
              <div className="nav-brand-text">
                <span>minik<span className="brand-hl">coder</span></span>
                <span className="brand-tag">ACADEMY</span>
              </div>
            </Link>
            <p className="footer-brand-about">
              Yapay zeka ile güçlendirilmiş, oyunlaştırılmış öğrenme platformu. 7–17 yaş arası çocuklar için Türkiye'nin en yenilikçi eğitim ekosistemi.
            </p>
            <div className="footer-social-links">
              <a href="#" className="footer-social-btn" aria-label="Instagram">📸</a>
              <a href="#" className="footer-social-btn" aria-label="YouTube">▶️</a>
              <a href="#" className="footer-social-btn" aria-label="LinkedIn">💼</a>
              <a href="#" className="footer-social-btn" aria-label="Twitter">🐦</a>
            </div>
          </div>

          <div>
            <div className="footer-col-heading">Platform</div>
            <ul className="footer-nav-list">
              <li><a href="#dersler">Ders Yelpazesi</a></li>
              <li><a href="#cinali">CinAli AI Öğretmen</a></li>
              <li><a href="#simulatur">Canlı Simülatör</a></li>
              <li><a href="#oyun">Oyun Merkezi & Rozetler</a></li>
              <li><Link to="/auth?mode=login">Giriş Yap</Link></li>
            </ul>
          </div>

          <div>
            <div className="footer-col-heading">Veliler & Okullar</div>
            <ul className="footer-nav-list">
              <li><a href="#veliler">Veli Paneli & Rapor</a></li>
              <li><Link to="/auth?mode=register-parent">Veli Kaydı</Link></li>
              <li><Link to="/auth?mode=school">Okul Yetkili Girişi</Link></li>
              <li><a href="#fiyatlar">Fiyatlandırma</a></li>
              <li><a href="#sss">Sıkça Sorulan Sorular</a></li>
            </ul>
          </div>

          <div>
            <div className="footer-col-heading">Kurumsal & Destek</div>
            <ul className="footer-nav-list">
              <li><a href="#iletisim">İletişim & Demo Talep</a></li>
              <li><a href="mailto:minikcoderakademi@gmail.com">minikcoderakademi@gmail.com</a></li>
              <li><a href="#">Gizlilik Politikası</a></li>
              <li><a href="#">Kullanım Koşulları</a></li>
              <li><Link to="/auth?mode=admin">Sistem Admin Girişi</Link></li>
            </ul>
          </div>
        </div>

        <div className="footer-sub-bottom">
          <div className="footer-copyright">
            © 2026 MinikCoder Academy. Tüm hakları saklıdır.
          </div>
          <div className="footer-legal-links">
            <a href="#">Gizlilik Politikası</a>
            <a href="#">Kullanım Koşulları</a>
            <a href="#">Çerez Politikası</a>
          </div>
        </div>
      </footer>
    </div>
  );
}